import { keepPreviousData, useQuery } from "@tanstack/react-query";
import * as Immutable from "immutable";
import fetch from "logic/rest/FetchProvider";
import { qualifyUrl } from "util/URLUtils";
import SearchExecutionState from "views/logic/search/SearchExecutionState";
import ParameterBinding from "views/logic/parameters/ParameterBinding";
import GlobalOverride from "views/logic/search/GlobalOverride";
import FieldTypeMapping from "views/logic/fieldtypes/FieldTypeMapping";
import EventsWidget from "views/logic/widgets/events/EventsWidget";
import InvestigationsWidget from "security-app/components/Investigations/plugin/SearchWidgets/logic/InvestigationsWidget";
import { MESSAGE_LIST_WIDGET_LIMIT } from "common/components/widgetRenderers/Constants";
import i18n from "i18n";
import { defaultOnError } from "util/conditional/onError";
const INITIAL_DATA = {
  result: null,
  calculatedAt: null,
  types: null,
  errorMessage: null,
  hasError: false
};
const getUrl = (exportCallId) => qualifyUrl(`plugins/org.graylog.plugins.widgetexport/widgetexport/${exportCallId}/values`);
const getTypeSpecificSearchTypes = (widgetType, searchTypesId) => {
  switch (widgetType) {
    case EventsWidget.type:
      return {
        [searchTypesId]: {
          page: 1,
          per_page: MESSAGE_LIST_WIDGET_LIMIT
        }
      };
    case InvestigationsWidget.type:
      return {
        [searchTypesId]: {
          page: 1,
          per_page: MESSAGE_LIST_WIDGET_LIMIT
        }
      };
    default:
      return {};
  }
};
const getExecutionState = (exportWidgetData, timerangeOverride = void 0, now = void 0) => {
  const parameterValues = exportWidgetData.parameter_values;
  const parameterBindings = Immutable.Map(
    Object.entries(parameterValues || {}).map(([key, { value }]) => [key, ParameterBinding.forValue(value)])
  );
  const searchTypes = getTypeSpecificSearchTypes(exportWidgetData.type, exportWidgetData.search_type_id);
  const globalOverrides = GlobalOverride.builder().searchTypes(searchTypes).timerange(timerangeOverride).now(now).build();
  return SearchExecutionState.create(parameterBindings, globalOverrides);
};
export const fetchWidgetValues = (exportCallId, executionState) => fetch("POST", getUrl(exportCallId), executionState).then((values) => {
  var _a, _b;
  return {
    calculatedAt: values.calculated_at,
    result: values.result,
    types: Immutable.List(values.field_types.map((json) => FieldTypeMapping.fromJSON(json))),
    hasError: ((_a = values.errors) == null ? void 0 : _a.length) > 0,
    errorMessage: (_b = values.errors) == null ? void 0 : _b.map((e) => e.description).join("\n")
  };
});
const useWidgetValues = (exportCallId, exportWidgetData, timerangeOverride, now, enabled) => {
  const { data, refetch, isFetching } = useQuery({
    queryKey: ["exported-widget-values", exportCallId, exportWidgetData == null ? void 0 : exportWidgetData.parameter_values, timerangeOverride, now],
    queryFn: () => defaultOnError(
      fetchWidgetValues(exportCallId, getExecutionState(exportWidgetData, timerangeOverride, now)),
      i18n.t("notifications.fetch_widget_values_failed", { ns: "enterprise_search" }),
      i18n.t("notifications.fetch_widget_values_failed_title", { ns: "enterprise_search" })
    ),
    placeholderData: keepPreviousData,
    enabled
  });
  return {
    data: data != null ? data : INITIAL_DATA,
    refetch,
    isFetching
  };
};
export default useWidgetValues;
